import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material'
import { Client, commentsApi } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'

interface QuickNoteDialogProps {
  open: boolean
  onClose: () => void
  client: Client | null
}

export default function QuickNoteDialog({ open, onClose, client }: QuickNoteDialogProps) {
  const { showSuccess, showError } = useNotification()
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!client || !note.trim()) return

    setSaving(true)
    try {
      await commentsApi.add(client.id, note.trim(), { entityType: 'client' })
      showSuccess('Note added successfully')
      setNote('')
      onClose()
    } catch (err) {
      console.error('Error adding note:', err)
      showError(err instanceof Error ? err.message : 'Failed to add note')
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    if (!saving) {
      setNote('')
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Note for {client?.name}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={4}
          label="Note"
          placeholder="Add a note about this client..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={saving}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !note.trim()}
        >
          {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Note'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
